// src/modules/app-views/leads/pages/Init/modals/ImportLeadsModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useState } from 'react';
import { Upload, Alert, Typography, Space, Flex, theme } from 'antd';
import { InboxOutlined, CheckCircleTwoTone } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import parseFile from './helpers/parseFile';
import autoMap from './helpers/autoMap';

const { Text } = Typography;
const { Dragger } = Upload;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const Step1Content = ({ onParsed }) => {
  const { token } = theme.useToken();
  const [parsedInfo, setParsedInfo] = useState(null);
  const [error, setError] = useState(null);

  const handleBeforeUpload = useCallback(
    async file => {
      setError(null);
      setParsedInfo(null);
      try {
        const result = await parseFile(file);
        const mapping = autoMap(result.headers);
        setParsedInfo({
          fileName: file.name,
          rowCount: result.rows.length,
          headerCount: result.columnsMeta.length,
        });
        onParsed({ ...result, fileName: file.name, mapping });
      } catch (err) {
        const key = err.message || 'file_parse_error';
        setError(key);
        appService.message({ flag: 'e', text: key });
      }
      return Upload.LIST_IGNORE;
    },
    [onParsed],
  );

  return (
    <Flex vertical gap="middle" className="mt-3">
      <Dragger
        accept=".csv,.xlsx,.json"
        multiple={false}
        showUploadList={false}
        beforeUpload={handleBeforeUpload}
        style={{ padding: 16 }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          {translateX('click_or_drag_file_to_upload')}
        </p>
        <p className="ant-upload-hint">
          {translateX('import_file_format_hint')}
        </p>
      </Dragger>

      {parsedInfo && (
        <Alert
          type="success"
          showIcon
          icon={<CheckCircleTwoTone twoToneColor={token.colorSuccess} />}
          title={parsedInfo.fileName}
          description={
            <Space orientation="vertical" size={0}>
              <Text>
                {translateX('rows_detected', { count: parsedInfo.rowCount })}
              </Text>
              <Text type="secondary">
                {translateX('columns_detected', {
                  count: parsedInfo.headerCount,
                })}
              </Text>
            </Space>
          }
        />
      )}

      {error && <Alert type="warning" showIcon title={translateX(error)} />}
    </Flex>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const importLeadsStep1 = ({ onParsed, hasParsedFile }) => ({
  stepTitle: 'select_file',
  content: () => <Step1Content onParsed={onParsed} />,
  // Custom onNext guards navigation: forbid advance if no file parsed yet.
  onNext: async () => hasParsedFile(),
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default importLeadsStep1;
